// tts-pronunciation.mixin.js

export const ttsPronunciationMixin = {
    methods: {
        /**
         * Improves text pronunciation for TTS systems
         * @param {string} text - The text to be pronounced
         * @returns {string} - Text with pronunciation improvements
         */
        improvePronunciation(text) {
            // Handle Roman numerals after names (kings, popes, etc.)
            const processRomanNumerals = (input) => {
                return input.replace(/\b([A-Za-z]+)\s+(I{1,3}|IV|V|VI{1,3}|IX|X|XI{1,3}|XIV|XV|XVI{1,3}|XIX|XX|XXX)\b/g, (match, name, numeral) => {
                    // Map from Roman numerals to ordinal words.
                    const romanMap = {
                        'II': 'the second',
                        'III': 'the third',
                        'IV': 'the fourth',
                        'V': 'the fifth',
                        'VI': 'the sixth',
                        'VII': 'the seventh',
                        'VIII': 'the eighth',
                        'IX': 'the ninth',
                        'XI': 'the eleventh',
                        'XII': 'the twelfth',
                        'XIII': 'the thirteenth',
                        'XIV': 'the fourteenth',
                        'XV': 'the fifteenth',
                        'XVI': 'the sixteenth',
                        'XVII': 'the seventeenth',
                        'XVIII': 'the eighteenth',
                        'XIX': 'the nineteenth',
                        'XX': 'the twentieth',
                        'XXX': 'the thirtieth'
                    };

                    // Use the ordinal word if available, otherwise keep the original
                    return `${name} ${romanMap[numeral] || numeral}`;
                });
            };

            // Process dates (e.g., "March 3" to "March third")
            const processDates = (input) => {
                const months = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ];

                // Include short month forms
                const shortMonths = [
                    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sept', 'Sep', 'Oct', 'Nov', 'Dec'
                ];

                // Combine all month formats
                const allMonths = [...months, ...shortMonths].join('|');

                // Map from numbers to ordinal words
                const ordinalMap = {
                    '1': 'first', '2': 'second', '3': 'third', '4': 'fourth', '5': 'fifth',
                    '6': 'sixth', '7': 'seventh', '8': 'eighth', '9': 'ninth', '10': 'tenth',
                    '11': 'eleventh', '12': 'twelfth', '13': 'thirteenth', '14': 'fourteenth', '15': 'fifteenth',
                    '16': 'sixteenth', '17': 'seventeenth', '18': 'eighteenth', '19': 'nineteenth', '20': 'twentieth',
                    '21': 'twenty-first', '22': 'twenty-second', '23': 'twenty-third', '24': 'twenty-fourth',
                    '25': 'twenty-fifth', '26': 'twenty-sixth', '27': 'twenty-seventh', '28': 'twenty-eighth',
                    '29': 'twenty-ninth', '30': 'thirtieth', '31': 'thirty-first'
                };

                // Pattern to match "Month day" (e.g., "March 3", "January 21")
                const datePattern = new RegExp(`\\b(${allMonths})\\s+(\\d{1,2})\\b`, 'g');

                return input.replace(datePattern, (match, month, day) => {
                    return `${month} ${ordinalMap[day] || day}`;
                });
            };

            // Process measurement units (e.g., "205g" to "205 grams")
            const processMeasurements = (input) => {
                // Define unit mappings (abbreviation to full word)
                const unitMappings = {
                    // Weight
                    'g': 'grams',
                    'kg': 'kilograms',
                    'mg': 'milligrams',
                    'oz': 'ounces',
                    'lb': 'pounds',
                    'lbs': 'pounds',

                    // Length
                    'mm': 'millimeters',
                    'cm': 'centimeters',
                    'dm': 'decimeters',
                    'm': 'meters',
                    'km': 'kilometers',
                    'ft': 'feet',
                    'yd': 'yards',
                    'mi': 'miles',

                    // Volume
                    'ml': 'milliliters',
                    'cl': 'centiliters',
                    'l': 'liters',
                    'dl': 'deciliters',
                    'gal': 'gallons',
                    'pt': 'pints',
                    'qt': 'quarts',
                    'fl oz': 'fluid ounces',

                    // Area
                    'm²': 'square meters',
                    'km²': 'square kilometers',
                    'cm²': 'square centimeters',
                    'mm²': 'square millimeters',
                    'ft²': 'square feet',
                    'in²': 'square inches',
                    'ac': 'acres',
                    'ha': 'hectares',

                    // Time
                    'ms': 'milliseconds',
                    'min': 'minutes',

                    // Temperature
                    '°C': 'degrees celsius',
                    '°F': 'degrees fahrenheit',
                    '°K': 'degrees kelvin',

                    // Data
                    'KB': 'kilobytes',
                    'MB': 'megabytes',
                    'GB': 'gigabytes',
                    'TB': 'terabytes',
                    'PB': 'petabytes',

                    // Speed
                    'mph': 'miles per hour',
                    'kph': 'kilometers per hour',
                    'km/h': 'kilometers per hour',
                    'm/s': 'meters per second',

                    // Pressure
                    'Pa': 'pascals',
                    'kPa': 'kilopascals',
                    'MPa': 'megapascals',
                    'psi': 'pounds per square inch',
                    'bar': 'bars',
                    'atm': 'atmospheres',

                    // Energy
                    'J': 'joules',
                    'kJ': 'kilojoules',
                    'cal': 'calories',
                    'kcal': 'kilocalories',
                    'Wh': 'watt hours',
                    'kWh': 'kilowatt hours',

                    // Power
                    'W': 'watts',
                    'kW': 'kilowatts',
                    'MW': 'megawatts',
                    'GW': 'gigawatts',
                    'hp': 'horsepower',

                    // Frequency
                    'Hz': 'hertz',
                    'kHz': 'kilohertz',
                    'MHz': 'megahertz',
                    'GHz': 'gigahertz'
                };

                // Sort unit keys by length (descending) to ensure longer units are processed first
                const sortedUnits = Object.keys(unitMappings).sort((a, b) => b.length - a.length);

                // Create the regex pattern with all units
                const unitsPattern = sortedUnits.map(unit => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');

                // Main regex pattern: number followed immediately by unit (no space in between)
                const measurementPattern = new RegExp(`(\\d+(\\.\\d+)?)\\s*(${unitsPattern})\\b`, 'g');

                // Replace all matches with expanded measurement text
                return input.replace(measurementPattern, (match, number, decimal, unit) => {
                    // Handle singulars for unit = 1
                    let unitText = unitMappings[unit] || unit;
                    if (parseFloat(number) === 1 && unitText.endsWith('s')) {
                        // Convert plural to singular for value of 1
                        unitText = unitText.slice(0, -1);
                    }

                    return `${number} ${unitText}`;
                });
            };

            // First process Roman numerals
            let improved = processRomanNumerals(text);

            // Then process dates
            improved = processDates(improved);

            // Process measurements
            improved = processMeasurements(improved);

            // Create a map of technical terms and their phonetic spellings
            // Format: actual term → how it should be pronounced as a word
            const pronunciationMap = {
                // Common tech companies and acronyms
                'FAANG': 'fang',
                'MAANG': 'mang',
                'GAFA': 'gafa',
                'MANGA': 'manga',
                'STAR': 'star',
                'CaaStle': 'castle',
                'SaaStr:': 'saastr',
                'Roblox': 'rowblox',

                //Famous people
                'Pichai': 'pitchchai',

                // AI models
                'LLaMA2': 'laa-muh-two',
                'LLaMA': 'laa-muh',
                'LLaMA3': 'laa-muh-three',
                'Llama': 'laa-muh',
                'Llama2': 'laa-muh-two',
                'Llama3': 'laa-muh-three',

                // REMOVED THE PROBLEMATIC AI RULES
                // " AI": 'a-i',
                // 'AI-': 'a-i',
                // 'AI': 'a-i',
                // '.ai': 'AI',
                // '.AI': 'AI',

                'DALL-E': 'dolly',

                // Databases
                'MySQL': 'my sequel',
                'PostgreSQL': 'post gress cue ell',
                'NoSQL': 'no sequel',

                // Scifi
                'SciFi': 'sigh fie',

                // Regional
                'PhonePe': 'phone pay',
                'Pokémon': 'po-kee-maan',
                'Pokemon': 'po-kee-maan',

                // Cloud & DevOps
                'K8s': 'kay-ates',
                'CUDA': 'koo-duh',

                // Programming languages & frameworks
                'Django': 'jango',
                'NumPy': 'num pie',
                'scikit-learn': 'sigh-kit-learn',

                // Common tech terms
                'SaaS': 'sass',
                'PaaS': 'pass',
                'IaaS': 'i as',
                'CORS': 'cors',
                'CAP': 'cap',
                'CRUD': 'crud',
                'REST': 'rest',
                'CAPTCHA': 'capcha',
                'OAuth': 'oauth',

                // Data formats
                'JSON': 'jay-sun',
                'YAML': 'yamal',

                // Hardware/infrastructure
                'RAM': 'ram',

                // Travel & transportation
                'BART': 'bart',

                // programming
                'asyncio': 'async-io',

                // special cases
                'h011yw00d': 'hollywood',
                'LiDAR': 'lidar',
                'LiDARs': 'lidars',
                'LIDAR': 'lidar',
                'Chibi': 'Cheebee',
                'h011yw00dAgent': 'hollywood agent',
                'a16z': 'A16z',
                'a16z.com': 'A16z dot com',
                'a16zcrypto.com': 'A16z crypto dot com',
                'a16zcrypto': 'A16z crypto',

                // general
                'IKEA': 'eye-key-uh',
                'IMDb': 'IMDB',
                'automating': 'automayting',
                '911': 'nine-one-one',
                'Uber': 'oo-ber',
                'NASA': 'nass-uh',
                'NATO': 'nay-toe',
                'UNESCO': 'unesco',
                'UNICEF': 'uniceff',
                'Nvidia': 'envidia',
                'NVIDIA': 'envidia',
                'Airbnb': 'Air BNB',

                // Other technical terms
                'GraphQL': 'graph quel',
                'SQL': 'sequel',
                'SDET': 'sdet',
                'FAISS': 'phize',
                'PACELC': 'pass-elk',
                'SIGCOMM': 'sig-comm',

                // Commonly mispronounced English words
                //'resume': 'resumay',
                //'resumes': 'resumays',
                //'résumé': 'resumay',
                'suite': 'sweet',
                'regex': 'redge-ex',
                'nginx': 'engine-x',
                'GUI': 'gooey',
                'IEEE': 'eye-triple-e',
                'iOS': 'eye-oh-ess',
                'macOS': 'mack-oh-ess',
                'Scala': 'ska-la',
            };

            // Sort the keys by length (desc) to ensure longer terms are replaced first
            // This prevents partial replacements (e.g., replacing "SQL" in "MySQL")
            const sortedTerms = Object.keys(pronunciationMap)
                .sort((a, b) => b.length - a.length);

            // Then apply the regular pronunciation map
            for (const term of sortedTerms) {
                // Create a regex that matches the term as a whole word
                const regex = new RegExp(`\\b${term}\\b`, 'gi');
                improved = improved.replace(regex, pronunciationMap[term]);
            }

            // Then handle all other AI occurrences (without using lookbehind)
            // This matches AI when it's surrounded by word boundaries, punctuation, or spaces
            improved = improved.replace(/(^|\s|:|;|,|\.|"|\(|')AI(\s|$|-|:|;|,|\.|"|\)|')/g, "$1AI$2");

            return improved;
        },

        /**
         * Enhanced version of getTextForSpeech that improves pronunciation
         * @param {string} content - HTML content
         * @returns {string} - Clean text with improved pronunciation
         */
        getEnhancedTextForSpeech(content) {
            // First clean the text using the original method
            const cleanedText = this.getTextForSpeech(content);

            // Then apply pronunciation improvements
            return this.improvePronunciation(cleanedText);
        }
    }
};
